// Context for the runtime stage catalogue. The Manage Team modal
// mutates the catalogue via /api/.../stages; every renderer that
// needs the current short / name / colour / hasHours / archived
// state reads from this provider.
//
// Falls back to the canonical STAGES constant before the first
// fetch resolves so the modal/UI render isn't blocked on the API.
import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  STAGES,
  type StageDefDto,
} from "../../../../../../shared/types/production-schedule";
import { useStages } from "../hooks/useProductionApi";

interface Ctx {
  stages: StageDefDto[];
  loading: boolean;
  refetch: () => Promise<void>;
}

// Convert the compile-time canonical STAGES into the same DTO shape
// the server returns, so consumers can read either source without
// branching.
function canonicalAsDtos(): StageDefDto[] {
  return STAGES.map((s, i) => ({
    stageKey: s.key,
    short: s.short,
    name: s.name,
    color: s.color,
    hasHours: s.hasHours,
    weld: Boolean(s.weld),
    bay: s.bay ?? null,
    sortOrder: i,
    archived: false,
    isCustom: false,
    // Fallback capacity before the API resolves — matches the
    // migration 0022 seed defaults.
    weeklyCapacityHours: s.hasHours ? (s.weld ? 80 : 40) : 0,
    updatedAt: new Date(0).toISOString(),
  }));
}

const StagesContext = createContext<Ctx>({
  stages: canonicalAsDtos(),
  loading: false,
  refetch: async () => {},
});

export function StagesProvider({ children }: { children: ReactNode }) {
  const { data, loading, refetch } = useStages();
  const value: Ctx = useMemo(() => ({
    stages: data?.stages ?? canonicalAsDtos(),
    loading,
    refetch,
  }), [data, loading, refetch]);
  return <StagesContext.Provider value={value}>{children}</StagesContext.Provider>;
}

// All consumers go through this hook so swapping the fallback (e.g.
// stale-while-revalidate) only needs one touch-point.
export function useStagesCatalogue(): Ctx {
  return useContext(StagesContext);
}

// Convenience: active (non-archived) stages in sort order. Most
// consumers want this; the full list is only needed by the Manage
// Team modal so it can render archived rows too.
export function useActiveStages(): StageDefDto[] {
  const { stages } = useStagesCatalogue();
  return useMemo(
    () => stages.filter((s) => !s.archived),
    [stages],
  );
}
